import type { CheckItem } from '@/content/checks'
import { published, type Draftable } from '@/content/drafts'
import { logoFor } from '@/content/customerLogos'
import type { Photo } from '@/content/sections'

/**
 * The customer success stories, and the page each card opens.
 *
 * The WordPress import brought `/customers/` across as **one paragraph holding
 * twenty-two anchors** — nothing else — so `LegacyPage` had two ways to draw it
 * and both were wrong: as a run of blue names in a row, or (once the wall of 55
 * marks landed underneath) suppressed entirely, which is what it does today. On
 * digitopia.co the same twenty-two are a grid of cards, each one a mark in a
 * white tile with the company name under it, and each card opens that company's
 * own story page (`/customers/intel-ai/`). Asked for on 2026-08-27 with a
 * screenshot of that grid: *"bu şekil tıklanabilir kutucuklar olmalı ve bu
 * kutucuklara tıkladığımızda ilgili success storinin kendi sayfasına
 * gitmeliyiz"*.
 *
 * Built like `content/reports.ts` and `content/ebooks.ts`, which answered the
 * same request for the reports and e-book indexes on the two previous days — a
 * typed catalogue and a designed template rather than twenty-two imported JSON
 * files, because a story page is a mark, a headline and a body and the block
 * model in `content/blocks.ts` has no shape for one.
 *
 * **Every slug here is confirmed, which is the one thing the e-books could not
 * say.** They are copied character for character out of the `href`s in
 * `imported/pages/en/customers.json` — the old site's own links to its own
 * pages — so an inbound link to `digitopia.co/customers/lc-waikiki/` lands on
 * `/en/customers/lc-waikiki` after the switch instead of a 404. That is a real
 * gain on top of the cards: those twenty-two addresses currently redirect into
 * nothing.
 *
 * **What is NOT here is the stories themselves.** The twenty-two story pages
 * were never among the 107 the importer pulled — the Customers page carried
 * only the links — and nothing in this repository holds their text. So every
 * page carries the company, its mark and the page's own lede, and says so in
 * amber: `storyChecks()` feeds the same channel the reports and e-books use, so
 * a production build **cannot publish them** until somebody supplies the
 * copy. Filling one in is a `body` and dropping its `check`.
 *
 * **Fourteen of them are now written: Petrol Ofisi Group, Arzum, ebebek, Intel AI,
 * PROVEN, Yazaki, A 101, LC Waikiki, AkçanSA, Assan Aluminium, Federal
 * Mogul, Netafim, Akcoat and Lila Kağıt** (the first two 2026-08-27,
 * *"petrol ofisi success story tasarım"* and *"arzum success story sayfası"*; the rest 2026-08-28,
 * *"bire bir aynı"* / *"birebir tıpa tıp aynısını tasarla"* / *"tastamam şekilde,
 * aynısını, tıpkısını tasarla"*, each with a
 * full-page capture of digitopia.co's own story page). Those pages
 * carry `intro` and/or `sections` — the standfirst under the header and the headed
 * sections the old page tells the story in — and `pages/Stories.tsx` draws them.
 * The other eight are untouched: no `sections`, so nothing below their header
 * changes, and their checks stand.
 *
 * Their checks stand too, in a narrower form. The words were **read off
 * screenshots**, not copied out of a file we hold, so the figures in them are the
 * kind of thing that has to be proofread against the source. Arzum's photography
 * was supplied first and is why `Story` has a `photo` — the wide picture the
 * reference runs under the standfirst; Arzum's own page no longer uses that
 * field, because its second, legible capture (2026-08-28) showed the picture
 * belonging *inside* "Who is Arzum?" rather than above it, but Petrol Ofisi and
 * ebebek do — and **ebebek's four arrived with its
 * capture**, which is why `StorySection` has one too: three of them sit inside
 * the story, one under a section and two beside one, and where each sits is part
 * of what the page says. (Only three were placed on the first pass; the fourth,
 * the one under the standfirst, landed on 2026-08-28 when the page was checked
 * against the capture a second time.) Petrol Ofisi's four followed on 2026-08-28 and needed
 * no new field: one under the standfirst, one under a section, two beside one.
 *
 * **Sixteen of them do have the story as a document**, sent on 2026-08-27 and
 * linked here as `download` — the "Read the full story (PDF)" button on the
 * page. They live in the `website-documents` bucket alongside the reports and
 * e-books, not in this repository. That closes "there is nowhere to read this"
 * and leaves the copy gap open, which is why the checks stay.
 *
 * **Nine of them also have a film**, sent on 2026-08-27 with a screenshot of
 * digitopia.co's own story header: the player sits in the header where the mark
 * would otherwise be, which is where the old site puts it. It is a `video` id
 * here and an iframe in `pages/Stories.tsx`. Like the PDF, it does not close the
 * copy gap — a film is not a page — so those checks stay open too.
 *
 * The nearest thing we hold for any of the rest is `imported/reference/en/yazaki-
 * digital-maturity-success-story.json`, and it is a *different* page — digitopia.co
 * serves it at `/yazaki-digital-maturity-success-story/`, not at
 * `/customers/yazaki/`. Yazaki's own page below is written from the four-part
 * capture of the `/customers/` address, so that file stays what it was: the record
 * of a second, differently-worded Yazaki page, named in the check rather than
 * spliced into this one.
 */
/**
 * One headed part of a written story.
 *
 * The shape digitopia.co's own story pages are built of, and the shape the
 * sixteen PDFs are written in: a heading, some paragraphs, often a list of
 * three, sometimes a closing paragraph after it. Deliberately not the block
 * model in `content/blocks.ts` — that is the record of what WordPress emitted
 * for the imported pages, and a story written here is not an imported page.
 */
export interface StorySection {
  /**
   * The section's own heading, as the story tells it.
   *
   * Optional, because A 101's reference (2026-08-28, asked for as an exact
   * copy) runs its two closing paragraphs under no heading at all — it is one
   * continuous piece of prose after the photograph, not a headed part. Every
   * story written before it has one on every section, and should: a headless
   * section is what the reference does, not a shortcut for not naming things.
   */
  heading?: string
  /** The paragraphs above the list, if there is a list. */
  body?: string[]
  /** The points under them: a plain sentence, or a bold lead and its sentence. */
  items?: { lead?: string; text: string }[]
  /**
   * Number the points instead of bulleting them.
   *
   * PROVEN's reference (2026-08-28, asked for as an exact copy) sets its
   * Results as `1.`–`6.` and its Solution as bullets, on the same page — so
   * the marker is a property of the list rather than of the template, and the
   * four stories written before it keep the bullets they were reviewed in.
   * A numbered list is a claim that the order matters; a story that means
   * "these six things, in no order" should stay bulleted.
   */
  ordered?: boolean
  /** The paragraphs after the list — a conclusion, not a second body. */
  after?: string[]
  /**
   * Draw this section in a grey panel rather than on the page.
   *
   * **Defaults to `true` since 2026-08-29**, when Penta Teknoloji's page — every
   * section a panel — was asked for as the shape of all of them. It used to be
   * opt-in, alternating with sections that ran loose on the page; that reads as
   * a rhythm on one page and as an inconsistency across twenty. Set `false` for
   * a section that genuinely belongs on the page.
   */
  band?: boolean
  /**
   * Centre the heading and the paragraphs, as digitopia.co's own pages do.
   *
   * **Defaults to `true` on a single-column section since 2026-08-29**, with
   * `band`, so that every story reads like Penta Teknoloji's. A two-column
   * section stays ranged left: a centred half-column has nothing to range
   * against, which is why Penta's own "Key Outcomes" is not centred. The list
   * stays left-aligned inside a centred section either way — centred bullets
   * are unreadable at any length. Set `false` for a section whose paragraphs
   * are long enough that a ragged left edge would cost the reader more than
   * the symmetry is worth.
   */
  centred?: boolean
  /**
   * A photograph belonging to this section.
   *
   * Distinct from `Story.photo`, which is the one wide picture under the
   * standfirst. The reference stories run pictures *inside* the story too —
   * ebebek has three — and where each one sits is part of what the page says,
   * so it is attached to the section it illustrates rather than to the story.
   */
  photo?: Photo
  /**
   * Where that photograph sits relative to the words. Default `below`.
   *
   * `left` and `right` make the section two columns; `below` runs it full
   * width under the words, which is how the reference breaks between its
   * problem sections and its answer sections.
   */
  photoSide?: 'left' | 'right' | 'below'
  /**
   * With `photoSide: 'right'`, draw the picture *inside* the grey panel.
   *
   * The default is ebebek's shape, and it stays the default: a panel holding
   * the words with the picture standing on the page beside it. Intel AI's
   * reference (2026-08-28) does the other thing — its "The Solution" panel
   * encloses both columns, which is simply the mirror of what `left` has
   * always done. So this is the flag that says "mirror the left shape", not a
   * new layout: only the side the picture falls on differs.
   *
   * Ignored without `band`, because there is no panel to be inside of.
   */
  photoInPanel?: boolean
}

export interface Story extends Draftable {
  /** Path under `customers/`, verbatim from the old page's own link. */
  slug: string
  /** The company, exactly as the imported page writes it — `logoFor` joins on this. */
  name: string
  /**
   * The page's H1, where the company's name is not what the reference sets.
   *
   * Defaults to `name`, which is what every story shipped with and what the
   * index cards and the `<title>` keep using either way. PROVEN's reference
   * (asked for again on 2026-08-29 as an exact copy) heads the page "PROVEN's
   * Digital Transformation Success Story", so the headline is a property of the
   * page rather than of the company — a story that has nothing else to say than
   * its own name should leave this out and get the name.
   */
  headline?: string
  /** What this company's story says. The page's lede until somebody writes it. */
  body: string[]
  /**
   * The standfirst under the header: who this company is, in one paragraph.
   *
   * Set apart from `body` because it is set apart on the page — a grey band
   * across the full width, before the story starts. Only the written stories
   * have one.
   *
   * An array where the reference runs more than one paragraph in that band, as
   * ebebek's does. A bare string is still a paragraph, so the two stories
   * written before it are untouched.
   */
  intro?: string | string[]
  /** The story itself. Absent on the nineteen still standing on the lede. */
  sections?: StorySection[]
  /**
   * One photograph of this customer's world, under the standfirst.
   *
   * Where the reference pages put it: the band that says who the company is, then
   * a wide picture of what they make, then the story. Petrol Ofisi's check names
   * the absence of exactly this — "the page reads correctly without them, but it
   * is a thinner page than the reference" — so it is a field rather than a one-off:
   * the next story that arrives with its photography gains it by adding a line,
   * and the stories without one render as they always have.
   */
  photo?: Photo
  /**
   * A photograph for the page header, in the slot the mark otherwise fills.
   *
   * Distinct from `photo`, which sits under the standfirst. The header column
   * holds one of three things and always has — the film where there is one,
   * otherwise the company's mark on the page. Intel AI's reference (2026-08-28)
   * runs a photograph there instead and no mark at all, so this is a third
   * value of a choice that already existed rather than a new slot on the page.
   *
   * Ignored where there is a `video`: the film owns that column, for the reason
   * `StoryPage` gives — one thing in the header, not a stack of two.
   */
  heroPhoto?: Photo
  /**
   * The words in the closing card, where this page's quote is not the one on
   * the Hall of Fame.
   *
   * `StoryBody` draws `testimonialForStory(slug)` at the foot, so a customer's
   * words live in one place and cannot drift into two versions — which is right
   * for every written page so far, because the reference's closing card and the
   * Hall of Fame card carry the same paragraph. Akcoat's do not: digitopia.co
   * runs the Hall of Fame quote as the *standfirst* at the top of that page and
   * closes on a different statement by the same person, about the company
   * rather than about the engagement. Without this the page would print one
   * paragraph twice and lose the other entirely.
   *
   * It overrides the quote only. The company, the mark, the portrait and the
   * attribution still come from `content/testimonials.ts`, so there is still
   * exactly one record of who this person is.
   */
  closingQuote?: string[]
  /**
   * The story as a document: the "Read the full story" PDF in the
   * `website-documents` bucket. Optional, because six of the twenty-two have no
   * file — a card is listed before its PDF exists, exactly as with the reports.
   */
  download?: string
  /**
   * The story as a film: the YouTube id of this company's success-story video,
   * played from the page header.
   *
   * The **id only**, never a watch URL — `storyEmbedSrc()` builds the embed
   * address from it, so the one place that decides which YouTube host and which
   * player parameters this site uses is a function rather than nine string
   * literals. Nine of the twenty-two have one (sent 2026-08-27); the rest keep
   * the mark in that slot.
   */
  video?: string
  /** An unresolved gap, rendered amber in a preview and gating production. */
  check?: string
}

/**
 * The heading over the logo wall below the grid.
 *
 * It exists *because* of the grid. Ten of these twenty-two companies also have a
 * mark on the wall, so without a line between the two sections the wall reads as
 * more cards that lost their captions — and TEMSA, Brisa and LC Waikiki appear
 * to be printed twice for no reason. Labelled, they are two claims: these have a
 * story you can read, and this is everyone.
 *
 * A restatement of the page's own title ("Great Companies Work with Digitopia")
 * rather than a new claim about the relationship.
 */
export const WALL_HEADING = 'Companies that work with Digitopia'

/** The Customers index's slug, in the one locale it exists in. */
export const STORIES_INDEX = 'customers'


/**
 * The player's address, from a video id.
 *
 * `youtube-nocookie.com` rather than `youtube.com`: the page embeds a third
 * party either way, and the no-cookie host is the one that does not set a
 * tracking cookie on a reader who never presses play. `rel=0` keeps the
 * end-of-video suggestions inside Digitopia's own channel instead of offering a
 * competitor's film on our customer's page.
 *
 * Built here rather than stored, because a `watch?v=` URL pasted into the
 * catalogue would render an iframe that YouTube refuses to frame — the failure
 * is a grey box, not an error, so the shape is not left to whoever adds the
 * tenth one.
 */
export function storyEmbedSrc(video: string): string {
  return `https://www.youtube-nocookie.com/embed/${video}?rel=0`
}

/**
 * Every story, one file each.
 *
 * WHY THIS IS NOT ONE ARRAY. The AI editor adds a story per request, and on
 * 2026-08-28 five of six page-build runs edited this file — so two agents
 * building two unrelated customer pages rewrote the same region and git refused
 * both rebases. Roughly half of all parallel runs were failing that way. A story
 * in its own file cannot collide with a story in another file.
 *
 * THAT FIRST SPLIT ONLY ADDED THE GLOB, and left the twenty-two existing
 * entries inline — where they kept growing: 189KB on 2026-08-28, 231KB two days
 * later once four of the one-line entries became full pages. Which fixed
 * collisions and did nothing for what the file costs to READ, the larger bill.
 * Measured 2026-08-30: an edit costs `context × turns × cache-read`, and the two
 * runs with real numbers on them were carrying **51,041 and 38,630 tokens of
 * context per turn**, 62% of the money. The biggest files in this repository are
 * the ones the editor touches most, and this was the biggest of them. A story
 * edit now reads 15KB at worst rather than 231KB.
 *
 * Add `src/content/stories/<slug>.ts`:
 *
 *     import { story } from '@/content/storyBuilder'
 *     export const added = story('acme', 'Acme', { doc: '...' })
 *     export const order = 22
 *
 * `story` comes from `storyBuilder`, never from here: this module is still
 * initialising when the glob evaluates that file, so importing it from here is
 * a `ReferenceError` at prerender. Dropping the re-export makes the wrong
 * import fail at typecheck instead.
 *
 * `order` is the hand-placed order the imported Customers page set — neither
 * alphabetical nor the wall's, and a marketing decision nobody may reshuffle,
 * which is why the twenty-two carry an explicit 0–21. **A file with no `order`
 * sorts last, not first**: the default is `Number.MAX_SAFE_INTEGER` rather than
 * the 0 it was while `CURATED` led the array by construction. Get that wrong and
 * every new story lands in the middle of the hand-placed ones. Ties break on
 * slug, so the output never depends on filesystem iteration order.
 */
const ADDED = import.meta.glob<{ added: Story; order?: number }>(
  './stories/*.ts',
  { eager: true },
)

/**
 * Every entry, drafts included. `STORIES` below is what ships.
 *
 * The split is deliberate and mirrors `LOCALES` / `ALL_LOCALES`: the plain
 * name is the published list, so a consumer that has never heard of drafts is
 * correct by default, and the only readers of this one are the build's draft
 * report and anything that genuinely needs the roster.
 */
export const ALL_STORIES: Story[] =
Object.values(ADDED)
  .filter((m) => m.added)
  .sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      || a.added.slug.localeCompare(b.added.slug),
  )
  .map((m) => m.added)

export const STORIES: Story[] = published(ALL_STORIES)


/**
 * The mark for a story, or nothing.
 *
 * **All twenty-two now have one** — the same join `LegacyPage` used to make on
 * this page, through `content/customerLogos.ts`, on the name as the old page
 * writes it. Ten came from the marketing set; five (Litehouse Foods, Penta
 * Teknoloji, Intel AI, Yazaki, PROVEN) were sent for these cards on 2026-08-27,
 * three more the same day (A 101, Arzum, Kordsa), and the last four that
 * afternoon: Borusan Otomotiv, Federal Mogul, Lila Kağıt and Serenas. That is why
 * twelve of them are in `customerLogos.ts` and not on the wall; the note there
 * says why.
 *
 * So `Plate`'s wordmark fallback in `pages/Stories.tsx` no longer fires. It stays
 * anyway: the mixed grid was never an apology, it is what the page should do when
 * the next customer arrives without a usable file, and dropping the card instead
 * would misrepresent the customer list to make the row look tidy.
 */
export function storyLogo(story: Story): { name: string; src: string } | undefined {
  return logoFor(story.name)
}

/**
 * The pages whose list of customers is the card grid rather than their own
 * blocks.
 *
 * Same provenance and the same shape as `PAGE_REPORTS` and `PAGE_EBOOKS` —
 * attached in `src/legacy.ts` at load time, not written into the imported JSON,
 * because that JSON is the record of what digitopia.co said and the grid is what
 * we are saying instead.
 */
export const PAGE_STORIES: Record<string, Story[]> = {
  [`en/${STORIES_INDEX}`]: STORIES,
}

/** Where a story's page lives, for a reader of a given locale. */
export function storyHref(locale: string, story: Story): string {
  return `/${locale}/${STORIES_INDEX}/${story.slug}`
}

/**
 * The story gaps, in the shape `build-manifest.json` records copy checks in, so
 * `pnpm verify` refuses a production build while a story page is still standing
 * on the index's lede instead of its own words. Without this they would be
 * visible in a preview and silently stripped from production, which is the exact
 * failure the check channel exists to prevent.
 */
export function storyChecks(): CheckItem[] {
  return STORIES.filter((s) => s.check).map((s) => ({
    locale: 'en',
    page: `${STORIES_INDEX}/${s.slug}`,
    section: 'story',
    text: s.check as string,
    // Missing copy that somebody has to go and fetch, not a claim somebody has
    // to stand behind — see `CheckItem` in content/checks.ts.
    severity: 'asset',
  }))
}

